/**
 * «Обслуживаем ли мы эту точку» — and everything else the city decides.
 *
 * One call answers far more than yes/no: it also carries the volume limits, how many cars fit in
 * one выезд, the delivery-window grid and the urgency surcharge. All of it is denormalized onto
 * the draft at check time, because every later step needs it and none of them should re-resolve
 * a pin to find out.
 */
import { apiRequest } from '@/shared/api/client';
import type { DeliveryWindow } from './model/deliveryWindow';
import type { ServiceHours } from './model/serviceHours';

interface OfferedWindowDto {
  from: string;
  to: string;
  day_offset: number;
  label: string;
  bookable_until?: string | null;
}

/** Часы работы пятна (`service_hours`, 01.10.2026). Те же «отсутствует, а не null» правила. */
interface ServiceHoursDto {
  label: string;
  asap_label: string;
  asap_available: boolean;
  asap_available_until?: string | null;
  asap_available_from?: string | null;
  asap_unavailable_text?: string | null;
}

/**
 * The wire shape, and it is **absent**, not null.
 *
 * Every field but `available` carries `skip_serializing_if = "Option::is_none"` on the server, so
 * an unset value is not sent at all. Typing them as `| null` was wrong and it showed: a zone with
 * no public name arrives with no `city_label` key, `undefined` sailed past a `=== null` guard, and
 * the address step said «Сюда приедем — undefined».
 *
 * Marked optional here so TypeScript forces the question at every use, and normalised to `null`
 * once, below — the rest of the app then has one shape to reason about instead of three.
 */
interface ServiceAreaDto {
  available: boolean;
  city_label?: string | null;
  delivery_windows?: OfferedWindowDto[] | null;
  dispatch_lead_minutes?: number | null;
  max_cars_per_order?: number | null;
  max_order_liters?: string | null;
  min_order_liters?: string | null;
  urgent_surcharge_fixed?: string | null;
  urgent_surcharge_percent?: string | null;
  service_hours?: ServiceHoursDto | null;
}

export interface ServiceArea {
  available: boolean;
  cityLabel: string | null;
  deliveryWindows: DeliveryWindow[];
  dispatchLeadMinutes: number | null;
  maxCarsPerOrder: number | null;
  maxOrderLiters: string | null;
  minOrderLiters: string | null;
  urgentSurchargeFixed: string | null;
  urgentSurchargePercent: string | null;
  /** `null` — точка вне зоны или бэкенд, который часов ещё не присылает. */
  serviceHours: ServiceHours | null;
}

/**
 * Coordinates are rounded to five decimals on the way out — about a metre.
 *
 * Not for privacy but for caching: the map fires a check after every camera settle, and full
 * float precision makes every pixel of drift a different cache key, so the debounce saves nothing.
 */
function round5(value: number): string {
  return value.toFixed(5);
}

export async function fetchServiceArea(lat: number, lon: number): Promise<ServiceArea> {
  const dto = await apiRequest<ServiceAreaDto>(
    `/v1/consumer/service-area?lat=${round5(lat)}&lon=${round5(lon)}`,
  );

  // `?? null` on every one of them, not just the one that broke: they are all the same kind of
  // optional, and fixing only the field that was noticed leaves the rest waiting their turn.
  return {
    available: dto.available,
    cityLabel: nonBlank(dto.city_label),
    deliveryWindows: (dto.delivery_windows ?? []).map((w) => ({
      from: w.from,
      to: w.to,
      dayOffset: w.day_offset,
      label: w.label,
      bookableUntil: w.bookable_until ?? null,
    })),
    dispatchLeadMinutes: dto.dispatch_lead_minutes ?? null,
    maxCarsPerOrder: dto.max_cars_per_order ?? null,
    maxOrderLiters: dto.max_order_liters ?? null,
    minOrderLiters: dto.min_order_liters ?? null,
    urgentSurchargeFixed: dto.urgent_surcharge_fixed ?? null,
    urgentSurchargePercent: dto.urgent_surcharge_percent ?? null,
    serviceHours:
      dto.service_hours === undefined || dto.service_hours === null
        ? null
        : {
            label: dto.service_hours.label,
            asapLabel: dto.service_hours.asap_label,
            asapAvailable: dto.service_hours.asap_available,
            asapAvailableUntil: dto.service_hours.asap_available_until ?? null,
            asapAvailableFrom: dto.service_hours.asap_available_from ?? null,
            asapUnavailableText: dto.service_hours.asap_unavailable_text ?? null,
          },
  };
}

/**
 * A name, or nothing.
 *
 * An empty string is treated as absent on purpose: it would render as «Сюда приедем — » with a
 * dangling dash, which is the same defect in a quieter costume. The label is decoration — whether
 * a point is served is `available`'s answer, never this field's.
 */
function nonBlank(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
